/**
 * De navigatie: een smalle verticale rail links in plaats van een balk bovenin.
 * Ingeklapt alleen iconen, uitgeklapt iconen met tekst; de stand blijft in
 * localStorage staan. Bovenin het woordmerk, daaronder de schermen, en
 * onderin Instellingen - daar horen het account en de app-brede voorkeuren.
 *
 * Onder het woordmerk staat de werkruimte (MA-72), met een wissel als het
 * account er meer dan één heeft. De schermen eronder zijn die van de werkruimte.
 */
import { WERKRUIMTE_LABELS, type Werkruimte } from '@mamimap/shared';
import {
  Database,
  Dumbbell,
  House,
  Map as MapIcon,
  PanelLeft,
  Settings,
  SquareKanban,
  Star,
  Tag,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { useRail } from '@/lib/instellingen';
import { Link, ROUTES, type Route, useNaarScherm, usePad } from '@/lib/router';
import { startpad, werkruimteVanPad } from '@/lib/werkruimte';

const BREED = 200;
const SMAL = 56;

const WERKRUIMTE_ICOON: Record<
  Werkruimte,
  ComponentType<{ className?: string; 'aria-hidden'?: boolean }>
> = {
  huizen: House,
  sportcity: Dumbbell,
};

export function Rail({ werkruimtes }: { werkruimtes: readonly Werkruimte[] }) {
  const pad = usePad();
  const naar = useNaarScherm();
  const [open, zetOpen] = useRail();
  const werkruimte = werkruimteVanPad(pad);
  const gedeeld = pad === ROUTES.instellingen;
  /* Op Instellingen de schermen van Huizen, tenzij het account daar niet in zit. */
  const toonSportcity = !werkruimtes.includes('huizen') || (werkruimte === 'sportcity' && !gedeeld);

  return (
    <nav
      aria-label="Hoofdnavigatie"
      style={{ width: open ? BREED : SMAL }}
      className="flex shrink-0 flex-col border-r border-line bg-surface-strong"
    >
      {/* Ingeklapt is het woordmerk zelf de knop die de rail weer opent: dan staat
          het merk bovenin zonder dat er een tweede icoon bij hoeft. */}
      <div className="flex h-12 items-center gap-2 border-b border-line px-2">
        <button
          type="button"
          onClick={() => zetOpen(!open)}
          aria-expanded={open}
          aria-label={open ? 'Menu inklappen' : 'Menu uitklappen'}
          className="railknop w-9 shrink-0 justify-center px-0"
        >
          {open ? (
            <PanelLeft className="h-4 w-4" aria-hidden="true" />
          ) : (
            <span className="kop text-[17px]" aria-hidden="true">
              M
            </span>
          )}
        </button>
        {open ? <span className="kop truncate text-[16px]">MamiMap</span> : null}
      </div>

      {werkruimtes.length > 1 ? (
        <div className="flex flex-col gap-1 border-b border-line p-2">
          {werkruimtes.map((naam) => (
            <Schermlink
              key={naam}
              naar={startpad(naam)}
              actief={!gedeeld && werkruimte === naam}
              icoon={WERKRUIMTE_ICOON[naam]}
              open={open}
            >
              {WERKRUIMTE_LABELS[naam]}
            </Schermlink>
          ))}
        </div>
      ) : null}

      {toonSportcity ? (
        <div className="flex flex-col gap-1 p-2">
          <Schermlink
            naar={ROUTES.sportcity}
            actief={pad === ROUTES.sportcity}
            icoon={MapIcon}
            open={open}
          >
            Kaart
          </Schermlink>
        </div>
      ) : (
        <div className="flex flex-col gap-1 p-2">
          <Schermlink
            naar={naar(ROUTES.kaart)}
            actief={pad === ROUTES.kaart}
            icoon={MapIcon}
            open={open}
          >
            Kaart
          </Schermlink>
          {/* Tussen de kaart en de datasets in: het is een selectie uit de kaart,
            geen administratie. */}
          <Schermlink
            naar={naar(ROUTES.kansjes)}
            actief={pad === ROUTES.kansjes}
            icoon={Star}
            open={open}
          >
            Kansjes
          </Schermlink>
          {/* Direct onder de kansjes: dezelfde woningen, per fase (MA-51). */}
          <Schermlink
            naar={naar(ROUTES.kanban)}
            actief={pad === ROUTES.kanban}
            icoon={SquareKanban}
            open={open}
          >
            Kanban
          </Schermlink>
          {/* Wat er aan labels bijkomt in het zoekgebied: huizen die eraan komen (MA-61). */}
          <Schermlink naar={ROUTES.labels} actief={pad === ROUTES.labels} icoon={Tag} open={open}>
            Labels
          </Schermlink>
          <Schermlink
            naar={naar(ROUTES.datasets)}
            actief={pad === ROUTES.datasets}
            icoon={Database}
            open={open}
          >
            Datasets
          </Schermlink>
        </div>
      )}

      {/* Onderin, met de rest van de hoogte ertussen. */}
      <div className="mt-auto border-t border-line p-2">
        <Schermlink
          naar={ROUTES.instellingen}
          actief={pad === ROUTES.instellingen}
          icoon={Settings}
          open={open}
        >
          Instellingen
        </Schermlink>
      </div>
    </nav>
  );
}

function Schermlink({
  naar,
  actief,
  icoon: Icoon,
  open,
  children,
}: {
  naar: Route | string;
  actief: boolean;
  icoon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  open: boolean;
  children: string;
}) {
  return (
    <Link
      naar={naar}
      aria-current={actief ? 'page' : undefined}
      title={open ? undefined : children}
      className={`railknop w-full ${open ? '' : 'justify-center px-0'}`}
    >
      <Icoon className="h-4 w-4 shrink-0" aria-hidden={true} />
      {open ? <span className="truncate">{children}</span> : null}
    </Link>
  );
}
